<template>
  <div class="operation-log">
    <div class="toolbar">
      <el-input
        v-model.trim="filters.username"
        size="small"
        clearable
        placeholder="用户名，不填为全部"
        class="filter-input"
        @clear="fetchData(true)"
        @keyup.enter.native="fetchData(true)"
      ></el-input>
      <el-select v-model="filters.action" size="small" placeholder="操作类型" class="filter-select">
        <el-option label="全部" value=""></el-option>
        <el-option label="登录" value="login"></el-option>
        <el-option label="新增" value="create"></el-option>
        <el-option label="编辑" value="update"></el-option>
        <el-option label="删除" value="delete"></el-option>
        <el-option label="导出" value="export"></el-option>
        <el-option label="其他" value="other"></el-option>
      </el-select>
      <el-select v-model="filters.success" size="small" clearable placeholder="结果" class="filter-select">
        <el-option label="成功" value="true"></el-option>
        <el-option label="失败" value="false"></el-option>
      </el-select>
      <el-date-picker
        v-model="filters.timeRange"
        size="small"
        type="datetimerange"
        range-separator="至"
        start-placeholder="开始时间"
        end-placeholder="结束时间"
        value-format="yyyy-MM-dd HH:mm:ss"
        class="filter-time"
      ></el-date-picker>
      <el-button type="primary" icon="el-icon-search" size="small" :loading="loading" @click="fetchData(true)">查询</el-button>
      <el-button icon="el-icon-refresh" size="small" :loading="loading" @click="resetFilters">重置</el-button>
    </div>

    <el-table :data="logs" size="small" border v-loading="loading">
      <el-table-column label="时间" width="165">
        <template slot-scope="scope">{{ formatTime(scope.row.createdAt) }}</template>
      </el-table-column>
      <el-table-column prop="username" label="用户" width="130" show-overflow-tooltip></el-table-column>
      <el-table-column label="类型" width="90" align="center">
        <template slot-scope="scope">
          <el-tag size="mini" :type="actionTagType(scope.row.action)">
            {{ actionText(scope.row.action) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="结果" width="80" align="center">
        <template slot-scope="scope">
          <el-tag size="mini" :type="scope.row.success ? 'success' : 'danger'">
            {{ scope.row.success ? '成功' : '失败' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="method" label="方法" width="80" align="center"></el-table-column>
      <el-table-column prop="path" label="接口" min-width="230" show-overflow-tooltip></el-table-column>
      <el-table-column prop="targetId" label="目标ID" min-width="150" show-overflow-tooltip></el-table-column>
      <el-table-column prop="ip" label="IP" min-width="130" show-overflow-tooltip></el-table-column>
      <el-table-column prop="durationMs" label="耗时" width="90" align="right">
        <template slot-scope="scope">{{ scope.row.durationMs }}ms</template>
      </el-table-column>
      <el-table-column label="详情" :width="isMobile ? 64 : 90" fixed="right">
        <template slot-scope="scope">
          <div class="table-actions">
            <el-button type="text" size="mini" @click="openDetail(scope.row)">查看</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      :page-sizes="[20, 50, 100]"
      :page-size="pageSize"
      :current-page="pageNum"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
    ></el-pagination>

    <el-dialog
      title="操作日志详情"
      :visible.sync="detailVisible"
      :width="isMobile ? '92%' : '760px'"
      :close-on-click-modal="false"
    >
      <el-descriptions v-if="currentLog" :column="2" size="small" border>
        <el-descriptions-item label="时间">{{ formatTime(currentLog.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="用户">{{ currentLog.username || '--' }}</el-descriptions-item>
        <el-descriptions-item label="操作">{{ actionText(currentLog.action) }}</el-descriptions-item>
        <el-descriptions-item label="结果">{{ currentLog.success ? '成功' : '失败' }}</el-descriptions-item>
        <el-descriptions-item label="IP">{{ currentLog.ip || '--' }}</el-descriptions-item>
        <el-descriptions-item label="耗时">{{ currentLog.durationMs }}ms</el-descriptions-item>
        <el-descriptions-item label="接口" :span="2">{{ currentLog.method }} {{ currentLog.path }}</el-descriptions-item>
        <el-descriptions-item label="User-Agent" :span="2">{{ currentLog.userAgent || '--' }}</el-descriptions-item>
        <el-descriptions-item v-if="!currentLog.success" label="错误" :span="2">
          {{ currentLog.errorCode }} {{ currentLog.errorMessage }}
        </el-descriptions-item>
      </el-descriptions>
      <pre class="request-summary">{{ prettyRequestSummary }}</pre>
    </el-dialog>
  </div>
</template>

<script>
import { getToken } from '@/utils/auth.js';

export default {
  name: 'OperationLog',
  data() {
    return {
      loading: false,
      logs: [],
      total: 0,
      pageNum: 1,
      pageSize: 20,
      filters: {
        username: '',
        action: '',
        success: '',
        timeRange: []
      },
      detailVisible: false,
      currentLog: null,
      isMobile: false
    };
  },
  computed: {
    prettyRequestSummary() {
      if (!this.currentLog) return '';
      return JSON.stringify(this.currentLog.requestSummary || {}, null, 2);
    }
  },
  created() {
    this.applyRouteQuery();
    this.fetchData();
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport);
  },
  watch: {
    '$route.query': {
      handler() {
        this.applyRouteQuery();
        this.fetchData(true);
      }
    }
  },
  methods: {
    authHeaders() {
      return {
        'x-auth-token': getToken()
      };
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
    },
    buildParams() {
      const params = {
        pageNum: this.pageNum,
        pageSize: this.pageSize
      };
      const username = String(this.filters.username || '').trim();
      if (username) params.username = username;
      if (this.filters.action) params.action = this.filters.action;
      if (this.filters.success) params.success = this.filters.success;
      if (this.filters.timeRange && this.filters.timeRange.length === 2) {
        params.startTime = this.filters.timeRange[0];
        params.endTime = this.filters.timeRange[1];
      }
      return params;
    },
    applyRouteQuery() {
      const query = this.$route.query || {};
      this.filters.username = query.username ? String(query.username) : '';
      this.filters.action = query.action ? String(query.action) : '';
      this.filters.success = query.success ? String(query.success) : '';
      if (query.startTime && query.endTime) {
        this.filters.timeRange = [String(query.startTime), String(query.endTime)];
      } else {
        this.filters.timeRange = [];
      }
    },
    fetchData(resetPage = false) {
      if (resetPage) this.pageNum = 1;
      this.loading = true;
      this.$axios.get('/operation-logs', {
        headers: this.authHeaders(),
        params: this.buildParams()
      }).then((res) => {
        const result = res.data.res || {};
        this.logs = result.list || [];
        this.total = result.total || 0;
        this.pageNum = result.pageNum || this.pageNum;
        this.pageSize = result.pageSize || this.pageSize;
      }).catch((err) => {
        this.$message.error(err.response?.data?.message || '操作日志加载失败');
      }).finally(() => {
        this.loading = false;
      });
    },
    resetFilters() {
      this.filters = {
        username: '',
        action: '',
        success: '',
        timeRange: []
      };
      this.fetchData(true);
    },
    handlePageChange(page) {
      this.pageNum = page;
      this.fetchData();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.fetchData(true);
    },
    openDetail(log) {
      this.currentLog = log;
      this.detailVisible = true;
    },
    formatTime(value) {
      return value ? this.$dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--';
    },
    actionText(action) {
      const map = {
        login: '登录',
        create: '新增',
        update: '编辑',
        delete: '删除',
        export: '导出',
        other: '其他'
      };
      return map[action] || action || '--';
    },
    actionTagType(action) {
      const map = {
        login: 'info',
        create: 'success',
        update: 'warning',
        delete: 'danger',
        export: '',
        other: ''
      };
      return map[action] || '';
    }
  }
};
</script>

<style scoped>
.operation-log {
  width: 100%;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.filter-input {
  width: 180px;
}

.filter-select {
  width: 130px;
}

.filter-time {
  width: 360px;
}

.pager {
  margin-top: 14px;
  text-align: right;
}

.request-summary {
  max-height: 320px;
  margin: 14px 0 0;
  padding: 12px;
  overflow: auto;
  color: var(--text-primary);
  background: var(--bg-page);
  border: 1px solid var(--border-lighter);
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.6;
}

.table-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.table-actions .el-button {
  margin: 0;
}

@media (max-width: 768px) {
  .filter-input,
  .filter-select,
  .filter-time {
    width: 100%;
  }

  .pager {
    text-align: left;
  }

  .table-actions {
    flex-direction: column;
    align-items: center;
  }
}
</style>
